import { $POST } from './request';
import useMessageStore, { EMessageStatus, IMessage } from '@/store/messageStore';
import { useSessionList } from './useChatConfig';
import SparkMD5 from 'spark-md5';
import { useNodes } from './useNodes';
import useUserStore, { sessionConfigCache } from '@/store/userStore';
import { errorMsg } from './serverCode';

export enum EFaqKnows {
  knows = 0,
  question = 1,
  answer = 2,
}

export const useChat = () => {
  const { nodes } = useNodes();
  const userStore = useUserStore();

  /**
   * handle send message
   */
  const msgSend = async ({
    msgBody,
    msgSeq,
    msgType,
    clientMsgID,
    sendTime,
  }: {
    msgBody: string;
    msgSeq: number;
    msgType: number;
    clientMsgID: string;
    sendTime: string;
  }): Promise<any> => {
    const sessionConfig = userStore.getSessionConfig();

    const resp = await $POST<{ msgReq: number }>(
      `/miner-api/trans/msg-send`,
      {
        msgBody,
        msgSeq,
        msgType,
        clientMsgID,
        sendTime,
      },
      {
        headers: {
          lbeSession: sessionConfig.sessionId,
        },
      }
    );

    if (resp.success) {
      userStore.setSendCount();
    }

    console.log('send..resp...', resp);

    // if (
    //   [
    //     errorMsg.sessionError.code,
    //     errorMsg.sessionFail.code,
    //     errorMsg.sessionTimeout.code,
    //   ].includes(resp.code as any)
    // ) {
    //   console.log('创建session失败');
    //   await createSession();
    // }

    return resp;
  };

  return {
    msgSend,
  };
};

export const useMessageHistory = () => {
  const messageStore = useMessageStore();
  const { sessionList, getSessionList, isLoading } = useSessionList();

  const [history, setHistory] = useState<IMessage[]>([]);
  const [moreHistory, setMoreHistory] = useState(true);

  const seqInfo = useRef({
    sessionList: [] as any,
    curInx: 0,
    curMsgSeq: 0,
    startSeq: 0,
    endSeq: 0,
    limit: 10,
  });

  const isEndCurSession = useMemo(() => {
    return seqInfo.current.startSeq === 1;
  }, [seqInfo.current.endSeq, seqInfo.current.limit]);

  const getHistory = async () => {
    let sessionId;
    if (seqInfo.current.curInx <= 0) {
      const { getSessionConfig } = sessionConfigCache();
      const { sessionConfig } = getSessionConfig();

      sessionId = sessionConfig?.sessionId;
    } else {
      sessionId = seqInfo.current.sessionList[seqInfo.current.curInx].sessionId;
    }

    if (!seqInfo.current.sessionList.length) {
      return [];
    }
    const end = seqInfo.current.sessionList[seqInfo.current.curInx].msgSeq;

    seqInfo.current.endSeq = end;
    const diff = end - seqInfo.current.limit + 1;
    seqInfo.current.startSeq = diff <= 0 ? 1 : diff;

    // if (History.length && history.length >= seqNumber.current.total) {
    //   setMoreHistory(false);
    //   return history;
    // }

    const resp = await $POST<{ content: any[]; total: number }>('/miner-api/trans/history', {
      seqCondition: {
        startSeq: seqInfo.current.startSeq,
        endSeq: seqInfo.current.endSeq,
      },
      sessionId: sessionId,
    });

    const res = resp.data?.content.map((item) => {
      return {
        ...item,
        isRead: item.status === 1,
        status: EMessageStatus.success,
        // clientMsgID: uuidV4() + '-' + getLocalTime(0).toString(),
      };
    });

    const resList = Array.isArray(res) ? [...res] : [];
    const result = resList.map((item) => {
      item.msgFromTime = 'history';
      return item;
    });
    setHistory(result);

    const noNextPageInCurSession = seqInfo.current.startSeq === 1;
    // 跨session
    if (noNextPageInCurSession) {
      seqInfo.current.curInx += 1;
    }

    return resList;
  };

  const getMoreHistory = async () => {
    // 判断是不是还在当前会话,
    // 如果是新的会话,则直接取 那个 session的 msgSeq
    const noNextPageInCurSession = seqInfo.current.startSeq - seqInfo.current.limit <= 0;

    const end =
      seqInfo.current.startSeq === 1
        ? seqInfo.current.sessionList[seqInfo.current.curInx].msgSeq
        : seqInfo.current.startSeq - 1;
    seqInfo.current.endSeq = end;

    const start = noNextPageInCurSession ? 1 : seqInfo.current.startSeq - seqInfo.current.limit;

    const resp = await $POST<{ content: any[]; total: number }>('/miner-api/trans/history', {
      seqCondition: {
        startSeq: start,
        endSeq: seqInfo.current.endSeq,
      },
      sessionId: seqInfo.current.sessionList[seqInfo.current.curInx].sessionId,
    });

    // After a successful request, overlay startSeq
    seqInfo.current.startSeq = start;

    const res = resp.data?.content
      .map((item) => {
        return {
          ...item,
          isRead: item.status === 1,
          status: EMessageStatus.success,
          // clientMsgID: item.clientMsgID || uuidV4() + '-' + '0',
        };
      })
      .filter((item) => item.clientMsgID);

    const result = res?.map((item) => {
      item.msgFromTime = 'history';
      return item;
    });

    // 跨session
    if (noNextPageInCurSession) {
      seqInfo.current.curInx += 1;
    }

    return result || [];
  };

  const msgCompensation = async () => {
    const newList = await getSessionList();
    const latest = newList?.[0];
    const latestSeq = latest?.msgSeq;
    if (latestSeq > messageStore.latestSeq) {
      const resp = await $POST<{ content: any[]; total: number }>('/miner-api/trans/history', {
        seqCondition: {
          startSeq: messageStore.latestSeq,
          endSeq: latestSeq,
        },
        sessionId: latest?.sessionId,
      });

      const res = resp.data?.content.map((item) => {
        return {
          ...item,
          isRead: item.status === 1,
          status: EMessageStatus.success,
          msgFromTime: 'history',
        };
      });

      const resList = [...messageStore.messageList, ...(res || [])];
      messageStore.setLatestSeq(latestSeq);
      messageStore.setMessageList(resList);
      messageStore.setScrollMessage(resList);
    }
  };

  return {
    history,
    moreHistory,
    seqInfo,
    isEndCurSession,
    getHistory,
    getMoreHistory,
    getSessionList,
    msgCompensation,
  };
};

export const useFaq = () => {
  const getFaqKnows = async (knowsTypes: EFaqKnows, id: string) => {
    const { getSessionConfig } = sessionConfigCache();
    const { sessionConfig } = getSessionConfig();
    const resp = await $POST(
      '/miner-api/trans/faq',
      {
        id,
        faqType: knowsTypes,
      },
      {
        headers: {
          lbeSession: sessionConfig?.sessionId,
        },
      }
    );

    console.log('faq...', resp);
  };

  return {
    getFaqKnows,
  };
};
